import { useForm } from "react-hook-form";
import { ILogInInterface } from "../interface/login.interface";
import { loginSchema } from "../validations/login.validation";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { HttpService } from "../../../../../services/base.service";
import { getIsLoading, getToken, getUser } from "../../../slice/user.selector";
import { useEffect } from "react";
import { routes } from "../../../../../router/routes";
import { Toast } from "../../../../../utils/Toast";
import { LoginUser } from "../../../slice/auth.thunks";
import { useState } from "react";
import storageService from "../../../../../utils/local-storage.utils";
import { userService } from "../../../../../services/user.service";
import { IApiErrorPayload, isEmailNotVerified, isTwoFactorRequired } from "../../../constants/auth-error.constants";
import { twoFactorService } from "../../../../../services/two-factor.service";
import { setLogin } from "../../../slice/user.slice";
import TwoFactorModal from "../../../components/TwoFactorModal";
import {useTranslation} from "react-i18next";

export const LoginForm = () => {
    const [isLoading, setIsloading] = useState(false);
    const dispatch = useDispatch();
    let history = useHistory();
    const token = useSelector(getToken);
    const {role} = useSelector(getUser)
    const { t } = useTranslation('', { keyPrefix: 'LoginPage' });

    useEffect(() => {
        const activeToken = token || storageService.getItem<string>('token');
        if (activeToken) {
            HttpService.setToken(activeToken);
            if(role == "USER"){
                history.push(routes.home);
            }else {
                history.push(routes.adminDashboard);
            }

        }
    }, [])
    const { register, handleSubmit, formState: { errors } } = useForm<any>({
        resolver: yupResolver(loginSchema)
    });
    const [challenge, setChallenge] = useState<IApiErrorPayload | null>(null);

    /** Redeems the code and finishes the login the challenge interrupted. */
    const completeTwoFactor = async (challengeToken: string, otpCode: string) => {
        const res: any = await twoFactorService.verify({ challengeToken, otpCode });
        dispatch(setLogin({ token: res.payload.token, user: res.payload.user }));
        Toast.fire({ icon: "success", title: 'Welcome back' });
        history.push(res.payload.user?.role === 'ADMIN' ? routes.adminDashboard : routes.home);
    };

    const onSubmit = async (data: ILogInInterface) => {
        setIsloading(true)
        data.email = data.email.toLowerCase();
        const res: any = await dispatch(LoginUser(data));
        const payload: IApiErrorPayload = res?.payload ?? {};

        if (payload.statusCode == 200) {
            setIsloading(false)
            Toast.fire({
                icon: "success",
                title: 'Welcome back'
            })
            history.push(routes.home);
            return;
        }

        // Checked before the email branch: credentials were accepted, so this
        // is a pending second factor rather than a failure.
        if (isTwoFactorRequired(payload)) {
            setIsloading(false)
            setChallenge(payload);
            return;
        }

        if (isEmailNotVerified(payload)) {
            Toast.fire({
                icon: 'warning',
                title: 'User not verified. Sending you a new verification code.'
            })
            // The verification screen can resend the code on demand, so a failed
            // resend must not strand the user on the login form.
            try {
                await userService.resendEmail2FA({ email: data.email });
            } catch (error) {
                Toast.fire({
                    icon: 'warning',
                    title: 'We could not send a new code. Use "Resend" on the next screen.'
                })
            }
            setIsloading(false)
            history.push(`/verify-email/${data.email}`);
            return;
        }

        setIsloading(false)
        Toast.fire({
            icon: 'warning',
            title: payload.message || 'Unable to sign in. Please try again.'
        })
    };
    if (challenge) {
        return (
            <TwoFactorModal
                challenge={challenge}
                onVerified={completeTwoFactor}
                onCancel={() => setChallenge(null)}
            />
        );
    }
    return (
        <form className="up-formtheme up-formsignin" onSubmit={handleSubmit(onSubmit as any)}>
            <fieldset>
                <div className="form-group">
                    <label>{t('emailAddress')}</label>
                    <input type="email"
                        {...register("email")} className="form-control" placeholder={t('enterEmail')} />
                    <small id="emailHelp" className="form-text text-muted">
                        {errors.email?.message}
                    </small>
                </div>
                <div className="form-group">
                    <label>{t('password')}</label>
                    <a className="up-btnforgotpassword" href="/forgot-password">{t('forgetPassword')}</a>
                    <input type="password"
                        {...register("password")}
                        className="form-control" placeholder={t('enterPassword')} />
                    <small id="emailHelp" className="form-text text-muted">
                        {errors.password?.message}
                    </small>
                </div>
                <div className="form-group">
                    <div className="up-remebermearea">
                        <input type="checkbox" name="checkbox" id="up-rememberme" />
                        <label htmlFor="up-rememberme">{t('rememberMe')}</label>
                    </div>
                    <button type="submit" className="up-btn up-btnwithloader">{t('signIn')} {isLoading && <div className="lds-dual-ring"></div>}</button>
                </div>
                <div className="up-alreadyahveaccount">
                    <h2>{t('noAccount')} <a href="/signup">{t('signUp')}</a></h2>
                </div>
            </fieldset>
        </form>
    )
};